import { RefreshCcw } from "lucide-react";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

export const DomainLoading = () => {
  const [isPremiumTemplate, setIsPremiumTemplate] = useState(() => {
    // Initialize immediately - don't wait for useEffect
    try {
      const domainType = localStorage.getItem('domainType');
      const storedTemplate = localStorage.getItem('activeTemplate');
      
      // Quick check: if WAAS domain, assume premium until proven otherwise
      if (domainType === 'WAAS') {
        return true;
      }
      
      if (storedTemplate) {
        const template = JSON.parse(storedTemplate);
        return template?.pages?.some(page => 
          page?.sections?.some(section => section?.type === 'waasPremiumHome')
        ) || false;
      }
    } catch (e) {
      // Silent fail
    }
    return false;
  });

  const [companyName, setCompanyName] = useState(() => {
    // Get company name immediately
    try {
      const company = JSON.parse(localStorage.getItem('company') || '{}');
      return company?.name || 'AVPS Technologies Inc';
    } catch (e) {
      return 'AVPS Technologies Inc';
    }
  });

  useEffect(() => {
    // Update if template or company changes
    const checkForPremiumTemplate = () => {
      try {
        const domainType = localStorage.getItem('domainType');
        const storedTemplate = localStorage.getItem('activeTemplate');
        
        // Check company name
        const company = JSON.parse(localStorage.getItem('company') || '{}');
        setCompanyName(company?.name || 'AVPS Technologies Inc');
        
        // Quick check: if WAAS domain
        if (domainType === 'WAAS') {
          setIsPremiumTemplate(true);
          return;
        }
        
        if (storedTemplate) {
          const template = JSON.parse(storedTemplate);
          const hasPremiumHome = template?.pages?.some(page => 
            page?.sections?.some(section => section?.type === 'waasPremiumHome')
          ) || false;
          
          setIsPremiumTemplate(hasPremiumHome);
        }
      } catch (e) {
        // Silent fail
      }
    };

    checkForPremiumTemplate();

    // Listen for template updates
    const handleTemplateUpdate = () => {
      checkForPremiumTemplate();
    };

    window.addEventListener('avps-template-updated', handleTemplateUpdate);
    window.addEventListener('storage', handleTemplateUpdate);

    return () => {
      window.removeEventListener('avps-template-updated', handleTemplateUpdate);
      window.removeEventListener('storage', handleTemplateUpdate);
    };
  }, []);

  // Premium template loading screen
  if (isPremiumTemplate) {
    return (
      <div
        style={{
          position: "fixed",
          inset: 0,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          background: "linear-gradient(135deg, #ebe9e1 0%, #d9d5c8 100%)",
          zIndex: 9999,
        }}
      >
        <div
          style={{
            fontFamily: "Inter, system-ui, -apple-system, sans-serif",
            fontSize: "clamp(28px, 4vw, 42px)",
            fontWeight: 300,
            letterSpacing: "-0.02em",
            color: "#15236f",
            textAlign: "center",
            marginBottom: "32px",
            animation: "avps-slide-up 0.6s ease-out",
          }}
        >
          {companyName}
        </div>
        <div
          style={{
            width: "48px",
            height: "48px",
            border: "3px solid rgba(21, 35, 111, 0.1)",
            borderTopColor: "#15236f",
            borderRadius: "50%",
            animation: "avps-spin 1s linear infinite",
          }}
        />
        <style>{`
          @keyframes avps-spin {
            to { transform: rotate(360deg); }
          }
          
          @keyframes avps-slide-up {
            from { 
              opacity: 0;
              transform: translateY(20px);
            }
            to { 
              opacity: 1;
              transform: translateY(0);
            }
          }
        `}</style>
      </div>
    );
  }

  // Default loading screen for other templates
  return (
    <div
      style={{
        alignItems: "center",
        background: "linear-gradient(to right, #e0f7fa, #f8f9fa)",
        display: "flex",
        flexDirection: "column",
        height: "100vh",
        justifyContent: "center"
      }}
    >
      <div
        style={{
          animation: "spin 1s ease-in-out infinite",
          border: "6px solid #e0e0e0",
          borderRadius: "50%",
          borderTop: "6px solid var(--flex-primary, var(--education-secondary, #3B9394))",
          boxShadow: "0 0 10px rgba(59, 147, 148, 0.3)",
          height: "60px",
          marginBottom: "20px",
          width: "60px"
        }}
      />
      <p
        style={{
          animation: "pulseTransform 1.8s ease-in-out infinite",
          color: "var(--flex-primary, var(--education-secondary, #3B9394))",
          fontFamily: "Segoe UI, sans-serif",
          fontSize: "20px",
          fontWeight: 600,
          letterSpacing: "1px"
        }}
      >
        Preparing your experience...
      </p>
      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        @keyframes pulseTransform {
          0% { opacity: 0.6; transform: scale(1) translateY(0); }
          50% { opacity: 1; transform: scale(1.05) translateY(-3px); }
          100% { opacity: 0.6; transform: scale(1) translateY(0); }
        }
      `}</style>
    </div>
  );
};

export const DomainError = () => {
  const handleRefresh = () => {
    window.location.reload();
  };

  return (
    <div
      style={{
        alignItems: "center",
        background: "#f4f6f8",
        display: "flex",
        flexDirection: "column",
        fontFamily: '"Segoe UI", sans-serif',
        height: "100vh",
        justifyContent: "center",
        padding: "2rem",
        textAlign: "center"
      }}
    >
      <div
        style={{
          animation: "fadeIn 0.6s ease-in-out",
          backgroundColor: "#fff",
          borderRadius: "20px",
          boxShadow: "0 12px 30px rgba(0, 0, 0, 0.1)",
          maxWidth: "800px",
          padding: "50px",
          width: "100%"
        }}
      >
        <h3
          style={{
            color: "#e74c3c",
            fontSize: "28px",
            fontWeight: 700,
            marginBottom: "24px"
          }}
        >
          Oops! Something went wrong
        </h3>
        <p
          style={{
            color: "#555",
            fontSize: "18px",
            lineHeight: "1.6",
            marginBottom: "36px"
          }}
        >
          We're having trouble loading this site at the moment.
        </p>
        <button
          onClick={handleRefresh}
          style={{
            alignItems: "center",
            backgroundColor: "var(--flex-primary, var(--education-secondary, #3B9394))",
            border: "none",
            borderRadius: "10px",
            color: "#fff",
            cursor: "pointer",
            display: "inline-flex",
            fontSize: "17px",
            fontWeight: "bold",
            gap: "10px",
            padding: "14px 30px",
            transition: "background 0.3s ease"
          }}
          type="button"
        >
          <RefreshCcw size={20} /> Refresh Page
        </button>
      </div>
      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(15px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
};

export const NotAuthorized = () => {
  const navigate = useNavigate();
  const goToHome = () => {
    navigate("/", { replace: true });
  };

  const goBack = () => {
    window.history.back();
  };

  return (
    <div
      style={{
        alignItems: "center",
        background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
        display: "flex",
        flexDirection: "column",
        fontFamily: '"Segoe UI", sans-serif',
        justifyContent: "center",
        minHeight: "100vh",
        padding: "2rem",
        textAlign: "center"
      }}
    >
      <div
        style={{
          backdropFilter: "blur(10px)",
          backgroundColor: "rgba(255, 255, 255, 0.95)",
          borderRadius: "24px",
          boxShadow: "0 20px 60px rgba(0,0,0,0.3)",
          maxWidth: "600px",
          padding: "60px",
          width: "100%"
        }}
      >
        <h1 style={{ color: "#2d3748", fontSize: "48px", fontWeight: 700, margin: "0 0 10px" }}>
          403
        </h1>
        <h2 style={{ color: "#4a5568", fontSize: "28px", fontWeight: 600, marginBottom: "20px" }}>
          Access Denied
        </h2>
        <p style={{ color: "#718096", fontSize: "18px", lineHeight: "1.6", marginBottom: "30px" }}>
          This page is not available for your current domain. Please contact support if you believe this is an error.
        </p>
        <div style={{ display: "flex", flexWrap: "wrap", gap: "15px", justifyContent: "center" }}>
          <button
            onClick={goBack}
            style={{
              backgroundColor: "#718096",
              border: "none",
              borderRadius: "12px",
              color: "white",
              cursor: "pointer",
              fontSize: "16px",
              fontWeight: 600,
              padding: "14px 28px",
              transition: "all 0.3s ease"
            }}
            type="button"
          >
            Go Back
          </button>
          <button
            onClick={goToHome}
            style={{
              backgroundColor: "#667eea",
              border: "none",
              borderRadius: "12px",
              color: "white",
              cursor: "pointer",
              fontSize: "16px",
              fontWeight: 600,
              padding: "14px 28px",
              transition: "all 0.3s ease"
            }}
            type="button"
          >
            Go to Home
          </button>
        </div>
      </div>
    </div>
  );
};

export const MainContent = () => null;
